/* Spotlight Contact — "Request a feature". Marquee headline, dark
   canvas, single-column dramatic form with cinema CTA. */

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSiteSettings } from "@/hooks/useSiteSettings";

export default function SpotlightContact() {
  const { settings } = useSiteSettings();
  const studioName = settings?.studio_name || "Studio";
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  useEffect(() => { document.title = `${studioName} — Request a Feature`; }, [studioName]);

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Add your name, email, and a short brief."); return; }
    setSending(true);
    try {
      await api.post("/contact", form);
      setForm({ name: "", email: "", phone: "", message: "" });
      toast.success("✦ Booking request received. The producer will reply within 48 hours.");
    } catch (err) { toast.error(err?.response?.data?.detail || "Transmission failed — try again."); }
    finally { setSending(false); }
  }

  return (
    <div data-testid="spotlight-contact" style={{ background: "var(--tenant-bg, #0A0A0F)", color: "var(--tenant-fg, #F5E9C9)" }}>

      <section className="px-6 lg:px-16 pt-20 pb-12 text-center border-b" style={{ borderColor: "color-mix(in srgb, var(--tenant-accent) 35%, transparent)" }}>
        <div className="text-[10px] uppercase tracking-[0.5em] mb-5" style={{ color: "var(--tenant-accent)" }}>
          ✦ Request a Feature
        </div>
        <h1 className="uppercase leading-[0.95]" style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(2.5rem, 7vw, 5rem)" }}>
          Tell us the story.
        </h1>
      </section>

      <div className="max-w-2xl mx-auto px-6 lg:px-16 py-20">
        <form onSubmit={onSubmit} className="space-y-7">
          {[
            { name: "name", label: "Your name", type: "text" },
            { name: "email", label: "Email", type: "email" },
            { name: "phone", label: "Phone (optional)", type: "tel" },
          ].map((f) => (
            <div key={f.name}>
              <label className="block text-[10px] uppercase tracking-[0.5em] mb-2" style={{ color: "var(--tenant-accent)" }}>
                ✦ {f.label}
              </label>
              <input type={f.type} value={form[f.name]}
                onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                data-testid={`spotlight-contact-${f.name}`}
                className="w-full bg-transparent border-b-2 py-3 outline-none focus:border-current"
                style={{ borderColor: "color-mix(in srgb, var(--tenant-accent) 45%, transparent)", fontFamily: "var(--tenant-serif)", fontSize: "1.125rem" }} />
            </div>
          ))}
          <div>
            <label className="block text-[10px] uppercase tracking-[0.5em] mb-2" style={{ color: "var(--tenant-accent)" }}>
              ✦ The brief
            </label>
            <textarea rows={6} value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              data-testid="spotlight-contact-message"
              className="w-full bg-transparent border-b-2 py-3 outline-none focus:border-current resize-none"
              style={{ borderColor: "color-mix(in srgb, var(--tenant-accent) 45%, transparent)", fontFamily: "var(--tenant-serif)", fontSize: "1.125rem" }} />
          </div>
          <div className="pt-6 text-center">
            <button type="submit" disabled={sending} data-testid="spotlight-contact-submit"
              className="inline-flex items-center gap-3 px-9 py-4 text-[10px] uppercase tracking-[0.5em] disabled:opacity-60"
              style={{ background: "var(--tenant-accent)", color: "var(--tenant-bg)" }}>
              {sending ? "Transmitting..." : "✦ Send to producer"}
            </button>
          </div>
        </form>

        {(settings?.contact_email || settings?.contact_location) && (
          <div className="mt-16 pt-8 border-t text-center space-y-2 text-[10px] uppercase tracking-[0.42em]" style={{ borderColor: "color-mix(in srgb, var(--tenant-accent) 35%, transparent)", color: "var(--tenant-fg-soft)" }}>
            <div>✦ Direct correspondence</div>
            {settings?.contact_email && (
              <div><a href={`mailto:${settings.contact_email}`} className="hover:underline" style={{ color: "var(--tenant-accent)" }}>{settings.contact_email}</a></div>
            )}
            {settings?.contact_location && <div>{settings.contact_location}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
